import { useState, useEffect, useRef, useCallback } from 'react';
import { getStoredUTM } from '../../../hooks/useUTM';

const PHONE = '421905929167';

const KAMIL_PHOTO =
  'https://readdy.ai/api/search-image?query=professional%20young%20man%20portrait%20friendly%20smile%20warm%20light%20clean%20background%20casual%20business%20confident%20web%20developer%20freelancer&width=80&height=80&seq=kamilfoto01&orientation=squarish';

// ─── Traffic source detection ────────────────────────────────────────────────

type TrafficSource =
  | 'google_ads'
  | 'google_organic'
  | 'social_ads'
  | 'social_organic'
  | 'linkedin'
  | 'email'
  | 'direct';

function detectTrafficSource(): TrafficSource {
  const utm = getStoredUTM();
  const src = (utm.utm_source ?? '').toLowerCase().trim();
  const medium = (utm.utm_medium ?? '').toLowerCase().trim();
  const referrer = (typeof document !== 'undefined' ? document.referrer : '').toLowerCase();

  const isSocialSrc = ['facebook', 'instagram', 'meta', 'fb', 'ig'].includes(src);
  const isPaidMedium = ['cpc', 'ppc', 'paid', 'paid_social', 'paidsearch'].includes(medium);
  const isOrganicMedium = ['organic', 'social', 'post', ''].includes(medium);

  if (src === 'google' && isPaidMedium) return 'google_ads';
  if (isSocialSrc && isPaidMedium) return 'social_ads';
  if (src === 'linkedin' || referrer.includes('linkedin.com')) return 'linkedin';
  if (src === 'email' || medium === 'email' || medium === 'newsletter') return 'email';
  if (
    (src === 'google' && (isOrganicMedium || medium === 'organic')) ||
    referrer.includes('google.')
  )
    return 'google_organic';
  if (
    isSocialSrc ||
    referrer.includes('facebook.com') ||
    referrer.includes('instagram.com') ||
    referrer.includes('t.co') ||
    referrer.includes('twitter.com')
  )
    return 'social_organic';

  return 'direct';
}

// ─── Message sets per source ──────────────────────────────────────────────────

interface Message {
  heading: string;
  body: string;
  cta: string;
}

const MESSAGE_SETS: Record<TrafficSource, Message[]> = {
  google_ads: [
    {
      heading: 'Reklama vás priviedla správne 🎯',
      body: 'Dobrý krok. Teraz urobíme ten ďalší - pozriem sa na váš projekt bezplatne.',
      cta: 'Napísať Kamilovi',
    },
    {
      heading: 'Hľadáte web alebo marketing?',
      body: 'Presne to riešim. Bez agentúry, bez zdĺhavého procesu - rovno k výsledkom.',
      cta: 'Rovno k veci',
    },
    {
      heading: 'Klikli ste - to niečo hovorí 💡',
      body: 'Ukážem vám, čo vám môžem reálne priniesť. Konzultácia zadarmo, bez záväzkov.',
      cta: 'Chcem vedieť viac',
    },
  ],

  google_organic: [
    {
      heading: 'Google vás priviedol správne 🔍',
      body: 'Web, ktorý predáva a nájde vás Google - to presne robím. Rád sa pozriem na váš projekt.',
      cta: 'Napísať Kamilovi',
    },
    {
      heading: 'Hľadali ste pomoc s webom alebo SEO?',
      body: 'Ste na správnej adrese. Organický rast nie je náhoda - je to systém. Preberieme.',
      cta: 'Porozprávame sa',
    },
    {
      heading: 'SEO a weby sú môj denný chlieb',
      body: 'Google sem poslal práve vás - nie náhoda. Napíšte, ukážem čo viem urobiť pre vás.',
      cta: 'Dohovoríme sa',
    },
  ],

  social_ads: [
    {
      heading: 'Videli ste ma v reklame 👋',
      body: 'Som skutočný - výsledky tiež. Napíšte, preberáme čo potrebujete.',
      cta: 'Napísať teraz',
    },
    {
      heading: 'Zaujala vás reklama na socke?',
      body: 'Mám rád, keď čísla hovoria za seba. Ukážem vám, ako to môže fungovať pre vás.',
      cta: 'Chcem to vidieť',
    },
    {
      heading: 'Reklama funguje - keď je správna 🎯',
      body: 'Nielen vytvoriť reklamu, ale nastaviť celý lievik. To je rozdiel. Porozprávajme sa.',
      cta: 'Rád vypočujem',
    },
  ],

  social_organic: [
    {
      heading: 'Videli ste ma na socke 😊',
      body: 'Super, že ste tu. Napíšte mi - rád sa pozriem na váš web alebo projekt.',
      cta: 'Napísať Kamilovi',
    },
    {
      heading: 'Social media vás priviedli',
      body: 'Weby, marketing, e-shopy - riešim to celé osobne. Dohovoríme sa na detailoch.',
      cta: 'Dohovoríme sa',
    },
    {
      heading: 'Sledujete ma online - to mi hovorí veľa',
      body: 'Seriózny záujem si zaslúži serióznu odpoveď. Som tu, napíšte.',
      cta: 'Porozprávame sa',
    },
  ],

  linkedin: [
    {
      heading: 'LinkedIn vás priviedol 💼',
      body: 'Firemné weby, B2B riešenia, digitálna stratégia - to je môj svet. Preberieme.',
      cta: 'Napísať Kamilovi',
    },
    {
      heading: 'Profesionálny prístup k vážnym projektom',
      body: 'Pracujem s firmami, ktoré myslia dlhodobo. Ak to sedí - napíšte.',
      cta: 'Chcem sa porozprávať',
    },
    {
      heading: 'Hľadáte partnera, nie dodávateľa?',
      body: 'Nie agentúru - niekoho kto skutočne rozumie biznisu a nesľubuje nereálne veci.',
      cta: 'To ma zaujíma',
    },
  ],

  email: [
    {
      heading: 'Prišli ste z e-mailu - výborne 📩',
      body: 'Som tu, keby ste chceli niečo prekonzultovať alebo sa spýtať na detaily.',
      cta: 'Napísať mi priamo',
    },
    {
      heading: 'Otvorili ste email - dobrý krok',
      body: 'Napíšte mi na WhatsApp, odpovedám rýchlejšie ako na email. Preberieme projekt.',
      cta: 'Napísať na WhatsApp',
    },
  ],

  direct: [
    {
      heading: 'Ahoj, som Kamil 👋',
      body: 'Chcete web, ktorý naozaj predáva? Rád sa na to pozriem - bezplatne, bez záväzkov.',
      cta: 'Napísať mi',
    },
    {
      heading: 'Máte web, ale bez zákazníkov?',
      body: 'Väčšina webov len existuje - nechajte ma pozrieť, kde sú rezervy. Konzultácia zadarmo.',
      cta: 'Chcem konzultáciu',
    },
    {
      heading: 'Malá firma aj veľký hráč 💼',
      body: 'Riešim projekty od jednoduchého webu po komplexný systém - vždy osobne, vždy na čas.',
      cta: 'Povedzte mi viac',
    },
    {
      heading: 'Nový web za 7 dní ⚡',
      body: 'Kým agentúra robí briefing, váš web môže byť live. Rýchlosť je moja výhoda.',
      cta: 'Ako to funguje?',
    },
    {
      heading: 'Rýchla otázka? Som tu 😊',
      body: 'Nemusíte hneď objednávať - stačí napísať, čo riešite. Odpoviem do pár minút.',
      cta: 'Napísať teraz',
    },
  ],
};

// ─── Timing constants ─────────────────────────────────────────────────────────
const INITIAL_DELAY_MS  = 14000; // first bubble after 14s from scroll
const BUBBLE_VISIBLE_MS = 11000; // visible for 11s
const REPEAT_EVERY_MS   = 55000; // next message every 55s

// ─── Component ────────────────────────────────────────────────────────────────

export default function FloatingWhatsApp() {
  const [scrolled, setScrolled]         = useState(false);
  const [bubbleOpen, setBubbleOpen]     = useState(false);
  const [dismissed, setDismissed]       = useState(false);
  const [msgIndex, setMsgIndex]         = useState(0);
  const [textIn, setTextIn]             = useState(false);
  const [pulse, setPulse]               = useState(true);
  const [source]                        = useState<TrafficSource>(() => detectTrafficSource());

  const messages = MESSAGE_SETS[source];
  const cycleRef = useRef(0);

  // Build WA URL with personalised pre-fill based on source
  const waText = encodeURIComponent(
    source === 'google_ads'      ? 'Dobrý deň, klikol som na reklamu a zaujalo ma to - rád by som sa spýtal na spoluprácu.' :
    source === 'google_organic'  ? 'Dobrý deň, našiel som vás cez Google - mám záujem o konzultáciu.' :
    source === 'social_ads'      ? 'Dobrý deň, videl som vašu reklamu na sociálnych sieťach - mám záujem o spoluprácu.' :
    source === 'social_organic'  ? 'Dobrý deň, sledoval som vás na sociálnych sieťach - rád by som sa porozprával.' :
    source === 'linkedin'        ? 'Dobrý deň, prišiel som cez LinkedIn - zaujíma ma spolupráca na firemnom projekte.' :
    source === 'email'           ? 'Dobrý deň, dostal som váš email a rád by som sa o niečom porozprával.' :
                                   'Dobrý deň, mám záujem o konzultáciu.'
  );
  const WA_URL = `https://wa.me/${PHONE}?text=${waText}`;

  // Scroll detection
  useEffect(() => {
    const onScroll = () => { if (window.scrollY > 300) setScrolled(true); };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Show bubble
  const showBubble = useCallback(() => {
    const idx = cycleRef.current % messages.length;
    cycleRef.current += 1;
    setMsgIndex(idx);
    setBubbleOpen(true);
    setTextIn(false);
    setTimeout(() => setTextIn(true), 120);
    return setTimeout(() => setBubbleOpen(false), BUBBLE_VISIBLE_MS);
  }, [messages.length]);

  useEffect(() => {
    if (!scrolled || dismissed) return;

    let autoHide: ReturnType<typeof setTimeout>;
    const init = setTimeout(() => {
      autoHide = showBubble();
    }, INITIAL_DELAY_MS);

    const repeat = setInterval(() => {
      if (!dismissed) {
        autoHide = showBubble();
      }
    }, REPEAT_EVERY_MS);

    return () => {
      clearTimeout(init);
      clearTimeout(autoHide);
      clearInterval(repeat);
    };
  }, [scrolled, dismissed, showBubble]);

  // Stop pulse
  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 8000);
    return () => clearTimeout(t);
  }, []);

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setBubbleOpen(false);
    setDismissed(true);
  };

  const currentMsg = messages[msgIndex];

  return (
    <div
      className={`fixed bottom-8 right-6 z-50 flex flex-col items-end gap-3 transition-all duration-500 ${
        scrolled ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12 pointer-events-none'
      }`}
    >
      {/* ── Chat bubble ── */}
      <div
        className={`flex items-end gap-2.5 transition-all duration-500 ${
          bubbleOpen && !dismissed
            ? 'opacity-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 translate-y-5 pointer-events-none'
        }`}
      >
        {/* Photo */}
        <div className="relative flex-shrink-0">
          <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-white shadow-lg">
            <img
              src={KAMIL_PHOTO}
              alt="Kamil Frajtko"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
        </div>

        {/* Bubble */}
        <div
          className={`relative max-w-[238px] bg-white rounded-2xl rounded-bl-md shadow-xl border border-zinc-100 px-4 py-3 transition-all duration-400 ${
            textIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          }`}
        >
          {/* Close */}
          <button
            onClick={handleDismiss}
            className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-zinc-200 hover:bg-zinc-300 rounded-full transition-colors duration-200 cursor-pointer"
            aria-label="Zavrieť"
          >
            <i className="ri-close-line text-zinc-600 text-xs" />
          </button>

          <div key={msgIndex}>
            <p className="text-zinc-800 text-sm font-bold leading-tight mb-1.5">
              {currentMsg.heading}
            </p>
            <p className="text-zinc-500 text-xs leading-relaxed mb-3">
              {currentMsg.body}
            </p>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setBubbleOpen(false)}
              className="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line" />
              {currentMsg.cta}
            </a>
          </div>

          {/* Tail */}
          <div className="absolute -bottom-2 left-5 w-0 h-0 border-l-[8px] border-r-[8px] border-t-[8px] border-l-transparent border-r-transparent border-t-white" />
        </div>
      </div>

      {/* ── Main button ── */}
      <a
        href={WA_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setBubbleOpen(false)}
        aria-label="Kontaktujte ma na WhatsApp"
        className="relative flex items-center justify-center w-14 h-14 rounded-full shadow-xl cursor-pointer transition-all duration-300 hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
      >
        {pulse && (
          <>
            <span
              className="absolute inset-0 rounded-full animate-ping"
              style={{ background: 'rgba(37,211,102,0.35)' }}
            />
            <span
              className="absolute inset-[-6px] rounded-full animate-ping"
              style={{ background: 'rgba(37,211,102,0.18)', animationDelay: '0.4s' }}
            />
          </>
        )}
        <i className="ri-whatsapp-line text-white text-2xl relative z-10" />
      </a>
    </div>
  );
}
